import { Injectable, EnvironmentInjector, inject, runInInjectionContext } from '@angular/core';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import { Observable, combineLatest } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { Habit, HabitCompletion, JournalEntry, Countdown, MigrationGuard, MandalaPrefs } from './challenge.model';

// Old data shape — read-only, only used by migration
interface OldTask { name: string; completed: boolean; }
interface OldCategory { name: string; tasks: OldTask[]; }
interface OldDayRecord { date: string; categories: OldCategory[]; }
interface OldWeeklyGoal {
  category: string;
  categoryIcon?: string;
  goalName: string;
  target: number;
  completions: string[];
  createdAt?: string;
  updatedAt?: string;
}
interface OldCountdown { date: string; }

export interface MigrationResult {
  habits: number;
  completions: number;
  weeklyGoals: number;
}

export interface ChallengeData {
  habits: Habit[];
  completions: HabitCompletion[];
  journals: { [date: string]: JournalEntry };
  countdown: Countdown | null;
  migration: MigrationGuard | null;
  mandalaPrefs: MandalaPrefs | null;
}

@Injectable({ providedIn: 'root' })
export class ChallengeService {
  private injector = inject(EnvironmentInjector);

  constructor(private db: AngularFireDatabase) {}

  private base(uid: string): string {
    return `users/${uid}/challenge`;
  }

  private inCtx<T>(fn: () => T): T {
    return runInInjectionContext(this.injector, fn);
  }

  subscribeAll(uid: string): Observable<ChallengeData> {
    const habits$ = this.inCtx(() => this.db.list<Omit<Habit, 'id'>>(`${this.base(uid)}/habits`).snapshotChanges().pipe(
      map(snaps => snaps.map(s => ({ id: s.key!, ...(s.payload.val() as Omit<Habit, 'id'>) })))
    ));
    const completions$ = this.inCtx(() => this.db.list<Omit<HabitCompletion, 'id'>>(`${this.base(uid)}/habit-completions`).snapshotChanges().pipe(
      map(snaps => snaps.map(s => ({ id: s.key!, ...(s.payload.val() as Omit<HabitCompletion, 'id'>) })))
    ));
    const journals$ = this.inCtx(() => this.db.list<Omit<JournalEntry, 'date'>>(`${this.base(uid)}/journal`).snapshotChanges().pipe(
      map(snaps => {
        const out: { [date: string]: JournalEntry } = {};
        snaps.forEach(s => { out[s.key!] = { date: s.key!, ...(s.payload.val() as Omit<JournalEntry, 'date'>) }; });
        return out;
      })
    ));
    const countdown$ = this.inCtx(() => this.db.object<Countdown>(`${this.base(uid)}/countdown`).valueChanges().pipe(
      map(v => v ?? null)
    ));
    const migration$ = this.inCtx(() => this.db.object<MigrationGuard>(`${this.base(uid)}/migration`).valueChanges().pipe(
      map(v => v ?? null)
    ));
    const mandalaPrefs$ = this.inCtx(() => this.db.object<MandalaPrefs>(`${this.base(uid)}/mandala-prefs`).valueChanges().pipe(
      map(v => v ?? null)
    ));

    return combineLatest([habits$, completions$, journals$, countdown$, migration$, mandalaPrefs$]).pipe(
      map(([habits, completions, journals, countdown, migration, mandalaPrefs]) => ({
        habits, completions, journals, countdown, migration, mandalaPrefs,
      }))
    );
  }

  setMandalaPrefs(uid: string, prefs: Omit<MandalaPrefs, 'updatedAt'>): Promise<void> {
    return this.inCtx(() => this.db.object(`${this.base(uid)}/mandala-prefs`).set({ ...prefs, updatedAt: Date.now() }));
  }

  addHabit(uid: string, habit: Omit<Habit, 'id'>): Promise<string> {
    return this.inCtx(() => this.db.list(`${this.base(uid)}/habits`).push(habit)).then(ref => ref.key!);
  }

  updateHabit(uid: string, habitId: string, patch: Partial<Omit<Habit, 'id'>>): Promise<void> {
    return this.inCtx(() => this.db.object(`${this.base(uid)}/habits/${habitId}`).update(patch));
  }

  archiveHabit(uid: string, habitId: string): Promise<void> {
    return this.updateHabit(uid, habitId, { archivedAt: Date.now() });
  }

  unarchiveHabit(uid: string, habitId: string): Promise<void> {
    return this.inCtx(() => this.db.object(`${this.base(uid)}/habits/${habitId}/archivedAt`).remove());
  }

  async deleteHabit(uid: string, habitId: string, allCompletions: HabitCompletion[]): Promise<void> {
    const toDelete = allCompletions.filter(c => c.habitId === habitId);
    const updates: { [path: string]: null } = {};
    for (const c of toDelete) {
      updates[`${this.base(uid)}/habit-completions/${c.id}`] = null;
    }
    updates[`${this.base(uid)}/habits/${habitId}`] = null;
    await this.db.database.ref().update(updates);
  }

  async toggleCompletion(uid: string, habit: Habit, date: string, existing: HabitCompletion | null): Promise<void> {
    if (existing) {
      await this.inCtx(() => this.db.object(`${this.base(uid)}/habit-completions/${existing.id}`).remove());
    } else {
      await this.inCtx(() => this.db.list(`${this.base(uid)}/habit-completions`).push({
        habitId: habit.id,
        date,
        createdAt: Date.now(),
      }));
    }
  }

  addCompletion(uid: string, habitId: string, date: string): Promise<string> {
    return this.inCtx(() => this.db.list(`${this.base(uid)}/habit-completions`).push({
      habitId,
      date,
      createdAt: Date.now(),
    })).then(ref => ref.key!);
  }

  removeCompletion(uid: string, completionId: string): Promise<void> {
    return this.inCtx(() => this.db.object(`${this.base(uid)}/habit-completions/${completionId}`).remove());
  }

  updateCompletionNote(uid: string, completionId: string, note: string): Promise<void> {
    const path = `${this.base(uid)}/habit-completions/${completionId}`;
    const trimmed = note?.trim() ?? '';
    if (trimmed) {
      return this.inCtx(() => this.db.object(path).update({ note: trimmed }));
    }
    return this.inCtx(() => this.db.object(`${path}/note`).remove());
  }

  upsertJournal(uid: string, date: string, patch: Partial<Omit<JournalEntry, 'date'>>): Promise<void> {
    const base = `${this.base(uid)}/journal/${date}`;
    const updates: { [path: string]: any } = {};
    for (const key of Object.keys(patch) as Array<keyof typeof patch>) {
      const value = patch[key];
      // Firebase rejects undefined; map nullish values to null so the field is deleted.
      updates[`${base}/${key}`] = value === undefined || value === null ? null : value;
    }
    updates[`${base}/updatedAt`] = Date.now();
    return this.db.database.ref().update(updates);
  }

  setCountdown(uid: string, countdown: Countdown): Promise<void> {
    return this.inCtx(() => this.db.object(`${this.base(uid)}/countdown`).set(countdown));
  }

  clearCountdown(uid: string): Promise<void> {
    return this.inCtx(() => this.db.object(`${this.base(uid)}/countdown`).remove());
  }

  async runMigrationIfNeeded(uid: string, currentGuard: MigrationGuard | null): Promise<MigrationResult | null> {
    if (currentGuard?.completedAt) {
      return null;
    }

    // Pre-flight: if any habits already exist in the new namespace, a previous
    // migration attempt partially succeeded. Bail rather than silently writing
    // duplicate habits.
    const existingHabits = await this.inCtx(() => this.db.list(`${this.base(uid)}/habits`)
      .snapshotChanges()
      .pipe(take(1), map(snaps => snaps.length))
      .toPromise());
    if ((existingHabits ?? 0) > 0) {
      throw new Error(
        'Migration aborted: habits already exist under users/{uid}/challenge/habits. ' +
        'A previous migration attempt may have partially succeeded. ' +
        'Manually clear users/{uid}/challenge in the Firebase console before retrying.'
      );
    }

    const [oldDays, oldWeekly, oldCountdown] = await Promise.all([
      this.inCtx(() => this.db.list<OldDayRecord>('/challenges').snapshotChanges().pipe(
        take(1),
        map(snaps => snaps.map(s => s.payload.val() as OldDayRecord))
      ).toPromise()).then(v => v ?? []),
      this.inCtx(() => this.db.list<OldWeeklyGoal>('/weeklyGoals').snapshotChanges().pipe(
        take(1),
        map(snaps => snaps.map(s => s.payload.val() as OldWeeklyGoal))
      ).toPromise()).then(v => v ?? []),
      this.inCtx(() => this.db.object<OldCountdown>('/challengeCountdown/global').valueChanges().pipe(
        take(1),
        map(v => v ?? null)
      ).toPromise()).then(v => v ?? null),
    ]);

    // Build habit table: unique (category, taskName) pairs across all old DayRecords.
    const habitIdByKey: { [key: string]: string } = {};
    const habitFirstDate: { [key: string]: string } = {};
    const habitKey = (category: string, name: string) => `${category}|||${name}`;

    for (const day of oldDays) {
      if (!Array.isArray(day.categories)) continue;
      for (const cat of day.categories) {
        if (!Array.isArray(cat.tasks)) continue;
        for (const task of cat.tasks) {
          const key = habitKey(cat.name, task.name);
          if (!habitFirstDate[key] || day.date < habitFirstDate[key]) {
            habitFirstDate[key] = day.date;
          }
        }
      }
    }

    // Write daily habits.
    for (const key of Object.keys(habitFirstDate)) {
      const [category, name] = key.split('|||');
      const createdAt = new Date(habitFirstDate[key] + 'T00:00:00').getTime();
      const id = await this.addHabit(uid, { name, category, frequency: 'daily', createdAt });
      habitIdByKey[key] = id;
    }

    // Write weekly habits and remember their ids.
    const weeklyHabitIdByGoal: { [goalName: string]: string } = {};
    for (const wg of oldWeekly) {
      const createdAt = wg.createdAt ? Date.parse(wg.createdAt) : Date.now();
      const id = await this.addHabit(uid, {
        name: wg.goalName,
        category: wg.category || '',
        frequency: 'weekly',
        target: wg.target,
        createdAt,
      });
      weeklyHabitIdByGoal[wg.goalName] = id;
    }

    // Write daily completions.
    let completionCount = 0;
    for (const day of oldDays) {
      if (!Array.isArray(day.categories)) continue;
      for (const cat of day.categories) {
        if (!Array.isArray(cat.tasks)) continue;
        for (const task of cat.tasks) {
          if (!task.completed) continue;
          const habitId = habitIdByKey[habitKey(cat.name, task.name)];
          if (!habitId) continue;
          await this.addCompletion(uid, habitId, day.date);
          completionCount++;
        }
      }
    }

    // Write weekly completions.
    let weeklyCompletionCount = 0;
    for (const wg of oldWeekly) {
      const habitId = weeklyHabitIdByGoal[wg.goalName];
      if (!habitId || !Array.isArray(wg.completions)) continue;
      for (const date of wg.completions) {
        await this.addCompletion(uid, habitId, date);
        weeklyCompletionCount++;
      }
    }

    // Write countdown.
    if (oldCountdown?.date) {
      await this.setCountdown(uid, { targetDate: oldCountdown.date });
    }

    const result: MigrationResult = {
      habits: Object.keys(habitFirstDate).length + oldWeekly.length,
      completions: completionCount + weeklyCompletionCount,
      weeklyGoals: oldWeekly.length,
    };

    // Write the guard last — only if everything else succeeded.
    await this.inCtx(() => this.db.object(`${this.base(uid)}/migration`).set({
      completedAt: Date.now(),
      itemsConverted: result,
    }));

    return result;
  }
}
